/**
 * Copilot file upload — sends an attached document to the backend extraction
 * endpoint and surfaces the extracted content / proposed actions in the
 * conversation.
 *
 *   POST /api/v1/copilot/sessions/{id}/files  (multipart: field "file")
 *     -> { message: {role:'assistant', content}, actions: CopilotAction[] }
 *
 * Extraction happens server-side (the copilot service turns the document text
 * into import/fill proposals). This helper mirrors the store's `sendMessage`
 * turn handling — create-session-if-needed, agent auto-run vs pending queue,
 * degraded-mode fallback — but for a file turn. It drives the (unowned)
 * copilot store through zustand's `getState()/setState()` since the store has
 * no dedicated upload action.
 */

import { ApiError, api, apiFetch } from '@/lib/api/client';
import { useCopilotStore } from '@/stores/copilot-store';

import { runRegisteredAction } from './action-executor';
import { getCurrent } from './context-registry';
import { normalizeActions } from './normalize';
import type { CopilotAction, CopilotMessage } from './types';

/** Fallback ceiling when the server config can't be read. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export interface CopilotUploadConfig {
  max_upload_mb: number;
  max_upload_bytes: number;
  accepted_types: string[];
}

let _uploadConfig: CopilotUploadConfig | null = null;

/**
 * The admin-configured upload limits (GET /api/v1/copilot/config), cached for
 * the session. Falls back to a sane default if the endpoint is unreachable so
 * the attach control still works.
 */
export async function getCopilotUploadConfig(): Promise<CopilotUploadConfig> {
  if (_uploadConfig) return _uploadConfig;
  try {
    _uploadConfig = await api.get<CopilotUploadConfig>('/api/v1/copilot/config');
  } catch {
    _uploadConfig = {
      max_upload_mb: 10,
      max_upload_bytes: MAX_UPLOAD_BYTES,
      accepted_types: ['pdf', 'docx', 'txt', 'csv', 'md', 'json'],
    };
  }
  return _uploadConfig;
}

interface SessionResponse {
  id: string;
}

interface FileResponse {
  message: { role: 'assistant'; content: string };
  actions?: CopilotAction[];
}

function uid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Uploads `file` to the copilot extraction endpoint and appends the resulting
 * assistant message + proposed actions to the conversation. Never throws:
 * a backend failure degrades to an honest system message and flips
 * `backendAvailable` off (which disables the attach control in the widget).
 */
export async function uploadCopilotFile(file: File): Promise<void> {
  const store = useCopilotStore;
  if (store.getState().sending) return;

  // Validate against the ADMIN-configured limit (server-enforced too, with 413).
  const cfg = await getCopilotUploadConfig();
  if (file.size > cfg.max_upload_bytes) {
    store.getState().pushToast(
      `Fichier trop volumineux (${(file.size / 1048576).toFixed(1)} Mo). Limite : ${cfg.max_upload_mb} Mo.`,
    );
    return;
  }

  const { mode } = store.getState();
  const pageContext = getCurrent();

  const userMessage: CopilotMessage = {
    id: uid(),
    role: 'user',
    content: `Fichier joint : ${file.name}`,
    at: nowIso(),
  };
  store.setState((s) => ({ messages: [...s.messages, userMessage], sending: true }));

  try {
    let sessionId = store.getState().sessionId;
    if (!sessionId) {
      const session = await api.post<SessionResponse>('/api/v1/copilot/sessions', {
        mode,
        page_context: pageContext ?? null,
      });
      sessionId = session.id;
      store.setState({ sessionId });
    }

    const form = new FormData();
    form.append('file', file);
    const response = await apiFetch<FileResponse>(
      `/api/v1/copilot/sessions/${sessionId}/files`,
      { method: 'POST', body: form },
    );

    const actions = normalizeActions(response.actions);
    const autoRun = actions.filter((a) => !a.requires_confirmation && mode === 'agent');
    const pending = actions.filter((a) => a.requires_confirmation || mode !== 'agent');

    const assistantMessage: CopilotMessage = {
      id: uid(),
      role: 'assistant',
      content: response.message.content,
      actions: actions.length > 0 ? actions : undefined,
      at: nowIso(),
    };

    store.setState((s) => ({
      messages: [...s.messages, assistantMessage],
      pendingActions: [...s.pendingActions, ...pending],
      backendAvailable: true,
      sending: false,
    }));

    for (const action of autoRun) {
      void runRegisteredAction(action);
    }
  } catch (err) {
    // A 413 (over the admin size limit) is a normal rejection, not a backend
    // outage — surface the reason and keep the control enabled.
    if (err instanceof ApiError && err.status === 413) {
      const detail = (err.body as { detail?: string } | null)?.detail;
      store.setState((s) => ({
        messages: [
          ...s.messages,
          {
            id: uid(),
            role: 'system',
            content: detail || `Fichier trop volumineux : « ${file.name} ».`,
            at: nowIso(),
          },
        ],
        sending: false,
      }));
      return;
    }
    // No local fallback for extraction — report honestly and disable the
    // control (backendAvailable → false) rather than faking a result.
    const sysMessage: CopilotMessage = {
      id: uid(),
      role: 'system',
      content: `Extraction indisponible — le backend copilot n’a pas pu traiter « ${file.name} ».`,
      at: nowIso(),
    };
    store.setState((s) => ({
      messages: [...s.messages, sysMessage],
      backendAvailable: false,
      sending: false,
    }));
  }
}
